/**
 * Turns one production's approved season into a downloaded `.xlsx`.
 *
 * The shape of the file is decided next door in `export-model.ts`, which knows
 * nothing about Firestore or ExcelJS. This is the half that talks to both: it
 * reads the two subcollections of every game in the selection, hands the lot to
 * `buildExport`, and renders the result.
 *
 * **ExcelJS is loaded on demand.** It is the better part of a megabyte and
 * nobody but an admin pressing one button ever needs it, so it is a dynamic
 * import and lands in its own chunk rather than in the bundle every viewer of
 * an on-air overlay downloads.
 */
import {Injectable} from '@angular/core';

import {EvaluationGame, EvaluationSeason} from '../../model/evaluation/evaluation';
import {AuthService} from '../../services/auth.service';
import {EvaluationsService} from '../../services/evaluations.service';
import {approvedGames, buildExport, ExportGameData, ExportWorkbook} from './export-model';
import {renderWorkbook} from './render-workbook';

@Injectable({providedIn: 'root'})
export class EvaluationExportService {

  constructor(
    private evaluations: EvaluationsService,
    private auth: AuthService,
  ) {
  }

  /**
   * Download the approved broadcasts of one production.
   *
   * `games` is the season as the caller already has it, so no game is read
   * twice; only the decisions and comments of the games that make the file are
   * fetched, and those in parallel — a season is forty-odd games and doing it
   * one at a time is forty round trips of waiting.
   *
   * Throws where there is nothing to export, so the caller can say so rather
   * than handing somebody an empty workbook.
   */
  async exportProducer(channelId: number,
                       games: EvaluationGame[],
                       season: EvaluationSeason): Promise<void> {
    const selected = approvedGames(games, channelId);
    if (!selected.length) {
      throw new Error('No approved broadcasts for this production in this season.');
    }
    const data: ExportGameData[] = await Promise.all(selected.map(async game => ({
      game,
      decisions: await this.evaluations.decisionsOnce(game.videoId),
      comments: await this.evaluations.commentsOnce(game.videoId),
    })));
    const user = this.auth.user;
    const workbook = buildExport(season, channelId, data, {
      by: user?.displayName ?? user?.email ?? '',
      at: new Date(),
      // Named rather than assumed: the timestamps in the file are rendered on
      // this clock, and a file sent abroad should say whose clock that was.
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    await this.download(workbook);
  }

  private async download(spec: ExportWorkbook): Promise<void> {
    // The browser bundle by name — see src/exceljs.d.ts for why this import is
    // not the plain `'exceljs'` one. esbuild wraps a UMD module as a CJS
    // namespace whose `default` is `module.exports`, so take either shape.
    const loaded: any = await import('exceljs/dist/exceljs.min.js');
    const excel = loaded?.Workbook ? loaded : loaded?.default;
    const book = renderWorkbook(excel, spec);
    const buffer = await book.xlsx.writeBuffer();
    this.save(new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }), spec.fileName);
  }

  /**
   * Hand the file to the browser.
   *
   * An object URL and a synthetic click: the blob never leaves the page, so
   * nothing about an invoice passes through a server on the way to the disk.
   * Revoked on the next tick — too early and Chrome cancels the download.
   */
  private save(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
